/*
 * Brand directory
 *
 * Resolving every vendor in one Liquid render blows Shopify's 5 second budget
 * on a large catalogue, so the list is fetched one letter at a time. Liquid
 * cannot read query parameters, so the letter travels as ?page= and the
 * section maps it back (page 2 = '#', page 3 = 'A' ... page 28 = 'Z').
 *
 * Fetched letters are kept in localStorage, so the second visit to the brands
 * page - or the first click on the nav dropdown after visiting it - paints
 * from cache with no network at all.
 *
 * Three modes share one element:
 *   section - alphabet is server rendered, then every uncached letter is
 *             requested at once behind skeleton placeholders and filtered
 *             locally. If the merchant has turned on server rendering the
 *             list is already there and nothing fetches.
 *   nav     - alphabet only, a letter is fetched on click
 *   fragment- the response to one of those fetches, never upgraded in place
 */
const BRAND_LETTERS = ['#'].concat('ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split(''))
const BRAND_SECTION_ID = 'brand-directory'
const BRAND_FETCH_RETRIES = 1
const BRAND_STORAGE_PREFIX = 'brand-directory:'
// One page past the alphabet, matching the section's own mapping.
const BRAND_MANIFEST_PAGE = BRAND_LETTERS.length + 2
// Stored under the same prefix as the letters, so pruning covers it too.
const BRAND_MANIFEST_KEY = 'letters'
const BRAND_SKELETON_WIDTHS = [82, 64, 91, 55, 74, 88, 61, 79, 68, 85]

class BrandDirectory extends HTMLElement {
  constructor() {
    super()
    this.mode = this.dataset.mode || 'section'
    this.alphabet = this.querySelector('.brand-directory__alphabet')
    this.results = this.querySelector('[data-brand-results]')
    this.emptyMessage = this.querySelector('[data-brand-empty]')
    this.defaultPanel = this.querySelector('[data-brand-default]')
    this.cacheVersion = this.dataset.cacheVersion || ''
    this.cacheTtl = Number(this.dataset.cacheTtl || 0) * 60 * 1000
    this.pending = new Map()
    this.activeLetter = null
  }

  connectedCallback() {
    if (this.mode === 'fragment' || !this.alphabet || !this.results) { return }

    this.fragmentBase = this.dataset.directoryUrl || window.location.pathname
    this.alphabet.addEventListener('click', this.onLetterClick.bind(this))
    this.pruneStorage()

    if (this.mode === 'nav') {
      // A cached manifest applies immediately. Otherwise it is fetched when the
      // menu is actually used - never as part of loading the page it sits on.
      if (this.readStored(BRAND_MANIFEST_KEY) !== null) {
        this.loadManifest()
      } else {
        const warm = () => this.loadManifest()
        this.addEventListener('mouseenter', warm, { once: true })
        this.addEventListener('focusin', warm, { once: true })
        this.addEventListener('touchstart', warm, { once: true, passive: true })
      }

      return
    }

    window.addEventListener('hashchange', () => this.applyHash())

    if (this.hasServerRenderedList()) {
      this.applyHash()
      return
    }

    this.loadAllLetters().then(() => this.applyHash())
  }

  hasServerRenderedList() {
    return this.results.querySelector('.brand-directory__group') !== null
  }

  static pageFor(letter) {
    // page 1 is the normal page render, so the alphabet starts at page 2.
    return BRAND_LETTERS.indexOf(letter) + 2
  }

  // The hash is the bare uppercase letter. '#' cannot be its own fragment, so
  // the non-alpha bucket gets a URL-safe token instead.
  static anchorFor(letter) {
    return letter === '#' ? '0-9' : letter.toUpperCase()
  }

  static letterFor(anchor) {
    return anchor === '0-9' ? '#' : anchor.toUpperCase()
  }

  enabledLetters() {
    return [...this.alphabet.querySelectorAll('a[data-brand-letter]')]
      .map(link => link.dataset.brandLetter)
      .filter(Boolean)
  }

  onLetterClick(event) {
    const trigger = event.target.closest('[data-brand-letter]')
    if (!trigger || !this.alphabet.contains(trigger)) { return }

    if (trigger.getAttribute('aria-disabled') === 'true') {
      event.preventDefault()
      return
    }

    // Let modified clicks open the underlying page as normal.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) { return }

    // This design has no "All" control, so the active letter toggles the
    // filter back off.
    const clicked = trigger.dataset.brandLetter || ''
    const letter = clicked === this.activeLetter ? '' : clicked
    event.preventDefault()

    if (this.mode === 'section') {
      // Jump the queue so a letter the visitor asked for is not stuck behind
      // the rest of the alphabet.
      if (letter) { this.loadLetter(letter).catch(() => {}) }
      this.filter(letter)
      const hash = letter ? `#${BrandDirectory.anchorFor(letter)}` : window.location.pathname
      window.history.replaceState(null, '', hash)
      return
    }

    this.renderLetter(letter).catch(() => {
      // No list to show, so fall back to the directory page itself.
      window.location.href = trigger.href
    })
  }

  applyHash() {
    const match = window.location.hash.match(/^#(0-9|[A-Za-z])$/)
    this.filter(match ? BrandDirectory.letterFor(match[1]) : '')
  }

  filter(letter) {
    const groups = this.results.querySelectorAll('.brand-directory__group')
    let visible = 0

    groups.forEach(group => {
      const show = !letter || group.dataset.letter === letter
      group.hidden = !show
      if (show) { visible += 1 }
    })

    // Skeletons for letters that are not on screen would be misleading.
    this.results.querySelectorAll('[data-letter-slot]').forEach(slot => {
      slot.hidden = Boolean(letter) && slot.dataset.letterSlot !== letter
    })

    this.setActive(letter)
    this.setEmpty(groups.length > 0 && visible === 0)
  }

  /* Storage --------------------------------------------------------------- */

  storageKey(letter) {
    return `${BRAND_STORAGE_PREFIX}${letter}`
  }

  // Every read and write is guarded: private browsing, a full quota and
  // blocked site data all throw rather than returning null.
  readStored(letter) {
    if (!this.cacheTtl) { return null }

    try {
      const raw = window.localStorage.getItem(this.storageKey(letter))
      if (!raw) { return null }

      const entry = JSON.parse(raw)
      if (entry.v !== this.cacheVersion) { return null }
      if (Date.now() - entry.t > this.cacheTtl) { return null }

      return entry.h
    } catch (error) {
      return null
    }
  }

  writeStored(letter, html) {
    if (!this.cacheTtl) { return }

    try {
      window.localStorage.setItem(this.storageKey(letter), JSON.stringify({
        v: this.cacheVersion,
        t: Date.now(),
        h: html
      }))
    } catch (error) {
      // Out of quota or storage unavailable. The in-flight result is still
      // used, we just will not have it next time.
    }
  }

  // Drops entries from an older catalogue so a changed vendor list does not
  // sit in storage until every letter happens to expire.
  pruneStorage() {
    try {
      const stale = []

      for (let i = 0; i < window.localStorage.length; i += 1) {
        const key = window.localStorage.key(i)
        if (!key || !key.startsWith(BRAND_STORAGE_PREFIX)) { continue }

        let entry = null
        try {
          entry = JSON.parse(window.localStorage.getItem(key))
        } catch (error) {
          stale.push(key)
          continue
        }

        if (!entry || entry.v !== this.cacheVersion) { stale.push(key) }
      }

      stale.forEach(key => window.localStorage.removeItem(key))
    } catch (error) {
      // Nothing to prune if storage cannot be read.
    }
  }

  /* Fetching -------------------------------------------------------------- */

  /* Letter manifest -------------------------------------------------------

     The nav renders its alphabet from vendor names alone, so a letter whose
     brands are all out of stock still looks available. The page discovers the
     truth by fetching every letter; the nav gets the same answer from one
     request instead of 27. */

  loadManifest() {
    if (this.manifestRequest) { return this.manifestRequest }

    const cached = this.readStored(BRAND_MANIFEST_KEY)
    if (cached !== null) {
      this.applyAvailableLetters(cached)
      this.manifestRequest = Promise.resolve(cached)
      return this.manifestRequest
    }

    const url = new URL(this.fragmentBase, window.location.origin)
    url.searchParams.set('section_id', BRAND_SECTION_ID)
    url.searchParams.set('page', String(BRAND_MANIFEST_PAGE))

    this.manifestRequest = fetch(url.toString())
      .then(response => {
        if (!response.ok) { throw new Error(`Brand directory: ${response.status}`) }
        return response.text()
      })
      .then(html => {
        const root = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector('brand-directory')

        const letters = root ? root.dataset.availableLetters : ''
        if (!letters) { throw new Error('Brand directory: empty letter manifest') }

        this.writeStored(BRAND_MANIFEST_KEY, letters)
        this.applyAvailableLetters(letters)
        return letters
      })
      .catch(error => {
        // Non-fatal: the letters stay as rendered and still correct themselves
        // when one is clicked and comes back empty.
        this.manifestRequest = null
        console.warn('Brand directory: letter manifest unavailable.', error)
      })

    return this.manifestRequest
  }

  applyAvailableLetters(letters) {
    this.alphabet.querySelectorAll('a[data-brand-letter]').forEach(link => {
      const letter = link.dataset.brandLetter
      if (!letter || letters.includes(`|${letter}|`)) { return }
      this.disableLetter(letter)
    })
  }

  fragmentUrl(letter) {
    const url = new URL(this.fragmentBase, window.location.origin)
    url.searchParams.set('section_id', BRAND_SECTION_ID)
    url.searchParams.set('page', String(BrandDirectory.pageFor(letter)))
    return url.toString()
  }

  // Each fragment carries the skipped-vendor entries for its own letter. The
  // page render resolves nothing, so this is the only place they come from.
  mergeDiagnostics(doc) {
    const list = this.querySelector('[data-brand-diagnostics-list]')
    if (!list) { return }

    const entries = doc.querySelectorAll('[data-brand-diagnostics-list] > li')
    if (!entries.length) { return }

    list.append(...[...entries].map(entry => entry.cloneNode(true)))
    this.querySelector('[data-brand-diagnostics]')?.removeAttribute('hidden')
  }

  parseFragment(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    this.mergeDiagnostics(doc)

    return [...doc.querySelectorAll('.brand-directory__group')].map(group => {
      // The page renders these ids too, so they cannot come along.
      group.removeAttribute('id')
      return group
    })
  }

  // Sending the whole alphabet at once makes an occasional throttle or dropped
  // connection likely, and a letter that fails is a letter the visitor never
  // sees, so each one gets a second chance before it gives up.
  fetchFragment(letter, attempt = 0) {
    return fetch(this.fragmentUrl(letter))
      .then(response => {
        if (!response.ok) { throw new Error(`Brand directory: ${response.status}`) }
        return response.text()
      })
      .catch(error => {
        if (attempt >= BRAND_FETCH_RETRIES) { throw error }

        return new Promise(resolve => window.setTimeout(resolve, 500 * (attempt + 1)))
          .then(() => this.fetchFragment(letter, attempt + 1))
      })
  }

  // Resolves to an array of group elements, empty when the letter has no
  // in-stock brands after all.
  loadLetter(letter) {
    if (this.pending.has(letter)) { return this.pending.get(letter) }

    const request = this.fetchFragment(letter)
      .then(html => {
        this.writeStored(letter, html)
        return this.parseFragment(html)
      })
      .catch(error => {
        this.pending.delete(letter)
        throw error
      })

    this.pending.set(letter, request)
    return request
  }

  /* Rendering ------------------------------------------------------------- */

  buildSkeleton(letter, index) {
    const slot = document.createElement('div')
    slot.className = 'brand-directory__skeleton'
    slot.dataset.letterSlot = letter
    slot.setAttribute('aria-hidden', 'true')

    // Rows vary in length so the placeholder reads as a list of names rather
    // than a block, and the count is derived from the letter so it does not
    // reshuffle between renders.
    const rows = 5 + (index % 4)

    for (let row = 0; row < rows; row += 1) {
      const bar = document.createElement('span')
      bar.className = 'brand-directory__skeleton-row'
      bar.style.width = `${BRAND_SKELETON_WIDTHS[(index + row) % BRAND_SKELETON_WIDTHS.length]}%`
      slot.append(bar)
    }

    return slot
  }

  // Requests the whole alphabet in one go, replacing each skeleton as its
  // letter lands. Anything already in storage is painted first, so a warm
  // cache means no request at all.
  loadAllLetters() {
    const letters = this.enabledLetters()
    if (!letters.length) { return Promise.resolve() }

    const slots = new Map()
    const outstanding = []

    letters.forEach((letter, index) => {
      const slot = this.buildSkeleton(letter, index)
      this.results.append(slot)
      slots.set(letter, slot)

      const cached = this.readStored(letter)
      if (cached === null) {
        outstanding.push(letter)
      } else {
        this.fillSlot(slots.get(letter), letter, this.parseFragment(cached))
      }
    })

    if (!outstanding.length) {
      this.filter(this.activeLetter || '')
      return Promise.resolve()
    }

    this.classList.add('brand-directory--loading')

    // Every letter goes out together, in alphabetical order, so the whole
    // directory arrives in one round trip's worth of wall clock rather than
    // seven staggered waves. Shopify serves these over HTTP/2, so they
    // multiplex on one connection instead of queueing behind a six-request
    // browser cap.
    return Promise.all(outstanding.map(letter => {
      return this.loadLetter(letter)
        .then(groups => this.fillSlot(slots.get(letter), letter, groups))
        .catch(error => {
          // Without this the skeleton just vanishes and the letter's brands
          // never appear, which is indistinguishable from the loader being
          // broken. The letter stays clickable, and loadLetter drops the
          // failed entry, so a click retries it.
          console.warn(`Brand directory: "${letter}" failed to load.`, error)
          slots.get(letter)?.remove()
        })
    })).then(() => {
      this.classList.remove('brand-directory--loading')
      this.filter(this.activeLetter || '')
    })
  }

  fillSlot(slot, letter, groups) {
    if (!slot || !slot.isConnected) { return }

    if (!groups.length) {
      // The cheap alphabet pass over-reported this letter: it has vendors, but
      // none of them resolved to a collection with stock.
      slot.remove()
      this.disableLetter(letter)
      return
    }

    slot.replaceWith(...groups.map(group => {
      const clone = group.cloneNode(true)
      clone.hidden = Boolean(this.activeLetter) && clone.dataset.letter !== this.activeLetter
      return clone
    }))
  }

  renderLetter(letter) {
    // The dropdown starts empty and returns to empty, rather than dumping the
    // whole directory into the menu.
    if (!letter) {
      this.results.replaceChildren()
      this.setActive('')
      this.setEmpty(false)
      return Promise.resolve()
    }

    const cached = this.readStored(letter)
    if (cached !== null) {
      this.showLetter(letter, this.parseFragment(cached))
      return Promise.resolve()
    }

    this.results.replaceChildren(this.buildSkeleton(letter, BRAND_LETTERS.indexOf(letter)))
    this.setActive(letter)
    this.classList.add('brand-directory--loading')

    return this.loadLetter(letter)
      .then(groups => {
        this.classList.remove('brand-directory--loading')
        this.showLetter(letter, groups)
      })
      .catch(error => {
        this.classList.remove('brand-directory--loading')
        this.results.replaceChildren()
        throw error
      })
  }

  showLetter(letter, groups) {
    if (!groups.length) {
      this.disableLetter(letter)
      this.results.replaceChildren()
      this.setActive('')
      this.setEmpty(true)
      return
    }

    this.results.replaceChildren(...groups.map(group => group.cloneNode(true)))
    this.setActive(letter)
    this.setEmpty(false)
  }

  /* State ----------------------------------------------------------------- */

  disableLetter(letter) {
    const link = this.alphabet.querySelector(`a[data-brand-letter="${CSS.escape(letter)}"]`)
    if (!link) { return }

    link.classList.add('brand-directory__letter--disabled')
    link.setAttribute('aria-disabled', 'true')
    link.setAttribute('tabindex', '-1')
  }

  setActive(letter) {
    this.activeLetter = letter || ''
    this.classList.toggle('brand-directory--filtered', Boolean(this.activeLetter))

    // The preloaded menu stands in for results only while nothing is selected.
    if (this.defaultPanel) {
      this.defaultPanel.hidden = Boolean(this.activeLetter)
    }

    this.alphabet.querySelectorAll('[data-brand-letter]').forEach(item => {
      const isActive = (item.dataset.brandLetter || '') === this.activeLetter
      item.classList.toggle('brand-directory__letter--active', isActive)

      if (isActive) {
        item.setAttribute('aria-current', 'true')
      } else {
        item.removeAttribute('aria-current')
      }
    })
  }

  setEmpty(isEmpty) {
    if (!this.emptyMessage) { return }
    // Never claim a letter is empty while its fragment is still in flight.
    this.emptyMessage.hidden = !isEmpty || this.classList.contains('brand-directory--loading')
  }
}

if (!window.customElements.get('brand-directory')) {
  window.customElements.define('brand-directory', BrandDirectory)
}
